import { get as _get } from 'lodash-es';
import { ERROR } from '@updater-core/lib/tracker';
import { DUKE_CODE, PGE_CODE } from '@mover-app/constants';
import {
  DEMO_PHONE_NUMBER,
  DEMO_PHONE_NUMBER_LINK,
  BRIDGEVINE_PHONE_NUMBER,
  BRIDGEVINE_PHONE_NUMBER_LINK,
  BRIDGEVINE_TRANSFER_PHONE_NUMBER,
  BRIDGEVINE_NO_OFFER_PHONE_NUMBER,
  BRIDGEVINE_TRANSFER_PHONE_NUMBER_LINK,
  BRIDGEVINE_NO_OFFER_PHONE_NUMBER_LINK,
  VERIZON_PHONE_NUMBER,
  VERIZON_PHONE_NUMBER_LINK,
  ATT_PHONE_NUMBER,
  ATT_PHONE_NUMBER_LINK,
  DUKE_PHONE_NUMBER,
  DUKE_PHONE_NUMBER_LINK,
  PGE_PHONE_NUMBER,
  PGE_PHONE_NUMBER_LINK,
  FEATURED_PROVIDER_TYPES,
  ANALYTICS_PROVIDER_TYPES,
  MOVING_SPECIALIST,
  HOMEPERKS_MOVING_SPECIALIST,
} from './constants';
import { getVariation } from '@updater-core/lib/optimizely-api';
import { generateCloudindaryUrl } from '@updater-core/utils/cloudinary';

// Conditional rendering helper for use with `hyperhtml`'s `wire`.
export const either = (bool, f, g) => (bool ? f() : g());
export const when = (bool, fn) => either(bool, fn, () => '');

// Lodash get doesn't handle null as we need to =
export const get = (obj, path, fallback) => {
  const val = _get(obj, path);

  // intentional to handle null
  // eslint-disable-next-line eqeqeq
  if (val != undefined) {
    return val;
  }

  if (typeof fallback === 'function') {
    return fallback();
  }

  return fallback;
};

function omitTypename(key, value) {
  return key === '__typename' ? undefined : value;
}

export const removeTypename = (variables) =>
  JSON.parse(JSON.stringify(variables), omitTypename);

export const mergeIntoArrayElementAt = (arr, index, value) => [
  ...arr.slice(0, index),
  { ...arr[index], ...value },
  ...arr.slice(index + 1),
];

export const handleError =
  (object, router, tracker) =>
  (error = { message: '', stack: '' }) => {
    tracker.track({
      verb: ERROR,
      object,
      details: { message: error.message, stack: error.stack },
    });
    router.go('/tv-internet/error');
  };

const exclusiveProvidersFromPropertyConfig = (propertyConfig) => {
  if (propertyConfig && propertyConfig.providers?.length) {
    return propertyConfig.providers.filter(
      (p) => p.featuredKind === 'property_exclusive'
    );
  }
  return [];
};

const featuredProvidersFromPropertyConfig = (propertyConfig) => {
  if (propertyConfig && propertyConfig.providers?.length) {
    return propertyConfig.providers.filter(
      (p) => p.featuredKind === 'property_featured'
    );
  }
  return [];
};

export const providersFromConfig = (propertyConfig) => ({
  exclusiveProviders: exclusiveProvidersFromPropertyConfig(propertyConfig),
  featuredProviders: featuredProvidersFromPropertyConfig(propertyConfig),
});

export const getUpdaterFeaturedProvider = (
  propertyConfig,
  availableProviders = []
) => {
  const { exclusiveProviders = [], featuredProviders = [] } =
    providersFromConfig(propertyConfig);

  if (exclusiveProviders.length > 0 || featuredProviders.length > 0) {
    return undefined;
  }

  return availableProviders.sort((p1, p2) => p2.valueScore - p1.valueScore)[0];
};

const whenDemo = {
  NAME: 'bridgevine',
  PHONE_NUMBER: DEMO_PHONE_NUMBER,
  PHONE_NUMBER_LINK: DEMO_PHONE_NUMBER_LINK,
  TRANSFER_PHONE_NUMBER: DEMO_PHONE_NUMBER,
  NO_OFFER_PHONE_NUMBER: DEMO_PHONE_NUMBER,
  TRANSFER_PHONE_NUMBER_LINK: DEMO_PHONE_NUMBER_LINK,
  NO_OFFER_PHONE_NUMBER_LINK: DEMO_PHONE_NUMBER_LINK,
};

const whenBridgevine = {
  NAME: 'bridgevine',
  PHONE_NUMBER: BRIDGEVINE_PHONE_NUMBER,
  PHONE_NUMBER_LINK: BRIDGEVINE_PHONE_NUMBER_LINK,
  TRANSFER_PHONE_NUMBER: BRIDGEVINE_TRANSFER_PHONE_NUMBER,
  NO_OFFER_PHONE_NUMBER: BRIDGEVINE_NO_OFFER_PHONE_NUMBER,
  TRANSFER_PHONE_NUMBER_LINK: BRIDGEVINE_TRANSFER_PHONE_NUMBER_LINK,
  NO_OFFER_PHONE_NUMBER_LINK: BRIDGEVINE_NO_OFFER_PHONE_NUMBER_LINK,
};

const whenVerizon = {
  NAME: 'bridgevine',
  PHONE_NUMBER: VERIZON_PHONE_NUMBER,
  PHONE_NUMBER_LINK: VERIZON_PHONE_NUMBER_LINK,
  TRANSFER_PHONE_NUMBER: VERIZON_PHONE_NUMBER,
  NO_OFFER_PHONE_NUMBER: VERIZON_PHONE_NUMBER,
  TRANSFER_PHONE_NUMBER_LINK: VERIZON_PHONE_NUMBER_LINK,
  NO_OFFER_PHONE_NUMBER_LINK: VERIZON_PHONE_NUMBER_LINK,
};

const whenAtt = {
  NAME: 'bridgevine',
  PHONE_NUMBER: ATT_PHONE_NUMBER,
  PHONE_NUMBER_LINK: ATT_PHONE_NUMBER_LINK,
  TRANSFER_PHONE_NUMBER: ATT_PHONE_NUMBER,
  NO_OFFER_PHONE_NUMBER: ATT_PHONE_NUMBER,
  TRANSFER_PHONE_NUMBER_LINK: ATT_PHONE_NUMBER_LINK,
  NO_OFFER_PHONE_NUMBER_LINK: ATT_PHONE_NUMBER_LINK,
};

const whenDuke = {
  NAME: 'bridgevine',
  PHONE_NUMBER: DUKE_PHONE_NUMBER,
  PHONE_NUMBER_LINK: DUKE_PHONE_NUMBER_LINK,
  TRANSFER_PHONE_NUMBER: DUKE_PHONE_NUMBER,
  NO_OFFER_PHONE_NUMBER: DUKE_PHONE_NUMBER,
  TRANSFER_PHONE_NUMBER_LINK: DUKE_PHONE_NUMBER_LINK,
  NO_OFFER_PHONE_NUMBER_LINK: DUKE_PHONE_NUMBER_LINK,
};

const whenPge = {
  NAME: 'bridgevine',
  PHONE_NUMBER: PGE_PHONE_NUMBER,
  PHONE_NUMBER_LINK: PGE_PHONE_NUMBER_LINK,
  TRANSFER_PHONE_NUMBER: PGE_PHONE_NUMBER,
  NO_OFFER_PHONE_NUMBER: PGE_PHONE_NUMBER,
  TRANSFER_PHONE_NUMBER_LINK: PGE_PHONE_NUMBER_LINK,
  NO_OFFER_PHONE_NUMBER_LINK: PGE_PHONE_NUMBER_LINK,
};

export const getPhoneNumber = ({ company, envName, getAvailableProviders }) => {
  if (envName === 'demo') return Promise.resolve(whenDemo);

  switch (company.code) {
    case DUKE_CODE:
      return Promise.resolve(whenDuke);
    case PGE_CODE:
      return Promise.resolve(whenPge);
    default:
      return getAvailableProviders.then((availableProviders) => {
        // This still goes to our own call centers, but Verizon requires
        // on-shore call centers only
        return availableProviders.some(({ id }) => id === 'verizon')
          ? whenVerizon
          : // This still goes to our own call centers, but AT&T traffic needs
          // to be shifted due to covid in Belize. The on-shore call center can
          // also handle AT&T hence the precedence in order.
          availableProviders.some(({ id }) => id === 'att')
          ? whenAtt
          : whenBridgevine;
      });
  }
};

export const getMovingSpecialist = ({ brandingDisplayName }) => {
  switch (brandingDisplayName.toLowerCase()) {
    case 'homeperks':
      return HOMEPERKS_MOVING_SPECIALIST;
    default:
      return MOVING_SPECIALIST;
  }
};

export const renderAnything = (fn) => {
  const el = document.createElement('div');
  fn(el);
  return el;
};

export const getAnalyticsProviderType = (providers) => {
  if (providers.some((provider) => provider.exclusive))
    return ANALYTICS_PROVIDER_TYPES.EXCLUSIVE;

  if (providers.some((provider) => provider.featured))
    return ANALYTICS_PROVIDER_TYPES.FEATURED;

  // when there is no client exclusive or featured providers, mark it as updater featured
  return ANALYTICS_PROVIDER_TYPES.UPDATER;
};

// deprecated for getProviderType
export const getFeaturedProviderType = (providers) => {
  const type = getAnalyticsProviderType(providers);

  return type === ANALYTICS_PROVIDER_TYPES.UPDATER
    ? FEATURED_PROVIDER_TYPES.NONE
    : type;
};

export const handleInaccurateServiceData = (offers) =>
  offers.map((offer) => {
    if (offer.provider.id.includes('spectrum') && offer.source !== 'scraped') {
      return {
        ...offer,
        hasInaccurateServiceData: true,
        services: {
          ...offer.services,
          internet: offer.services.internet
            ? {
                ...offer.services.internet,
                speed: {
                  ...(offer.services.internet.speed || {}),
                  inaccurateMbps: '30-200',
                  baseMbps: 30,
                },
              }
            : offer.services.internet,
        },
      };
    }

    return offer;
  });

export const showVerizonOffers = async (
  isFeatureEnabled,
  getAvailableProviders
) => {
  const availableProviders = (await getAvailableProviders) || [];

  if (availableProviders.some(({ id }) => id === 'verizon')) {
    return isFeatureEnabled('TV_Internet_Show_Verizon_Offers');
  }

  return false;
};

const defaultLowestValueScore = 65;
const featured1ValueScore = 92;
const featured2ValueScore = 91;

const providerValueScores = {
  verizon: 89,
  att: 87,
  optimum: 86,
  'optimum-cablevision': 86,
  spectrum: 85,
  'spectrum-brighthouse': 85,
  'spectrum-charter': 85,
  'spectrum-twc': 85,
  cox: 84,
  xfinity: 88,
  suddenlink: 84,
  directv: 83,
  'adt-home-security': 83,
  centurylink: 82,
  'vivint-home-security': 82,
  frontier: 82,
  armstrong: 81,
  'atlantic-broadband': 81,
  bendbroadband: 81,
  'duke-energy': 81,
  'protect-america': 81,
  windstream: 81,
  'windstream-cavalier': 81,
  dish: 80,
  'cincinnati-bell': 80,
  mediacom: 78,
  wow: 78,
  'grande-communications': 77,
  'buckeye-broadband': 76,
  'cable-one': 76,
  cablelabs: 76,
  earthlink: 76,
  gci: 76,
  hughesnet: 76,
  onesource: 76,
  rcn: 76,
  'rise-broadband': 76,
  tds: 76,
  viasat: 76,
  mctv: 75,
  megapath: 72,
  pixius: 72,
  constellation: 71,
  covad: 71,
  'earthlink-business': 71,
  midco: 71,
  nuvox: 71,
  primus: 71,
  calis: 70,
  fws: 70,
  viaero: 70,
  one: 69,
  vodafone: 69,
  '8x8': 68,
  vonage: 68,
};

/* Logic and values copied from home services stack
 * services/stacks/homeservices/offer/value_score/generator.js
 */
const calculateValueScore = ({ code }, propertyConfig = {}) => {
  const { providers = [] } = propertyConfig;
  const [featured1, featured2] = providers;
  if (code && featured1 && code === featured1.id) return featured1ValueScore;
  if (code && featured2 && code === featured2.id) return featured2ValueScore;
  return providerValueScores[code] || defaultLowestValueScore;
};

/* Values copied from home services stack
 * services/stacks/homeservices/offer/providers/index.js
 * Hardcoding is temporary until we come up with better logic in serviceability
 */
const showableNotAvailableProviders = [
  // 'Spectrum', // Removing this temporarily until BE handles multiple Spectrums
  'AT&T',
  'Xfinity',
  'Optimum',
  'CenturyLink',
  'Frontier',
  'Verizon',
];

export const serviceabilityPresenter = async ({
  offers,
  providerSummaries,
  propertyConfig,
}) => {
  const providerInfo = providerSummaries.reduce((info, summary) => {
    const { availability, provider } = summary || {};
    const { code: id, displayName, name, logo } = provider || {};
    if (availability === 'available') {
      return {
        ...info,
        availableProviders: [
          ...(info.availableProviders || []),
          {
            id,
            name: displayName || name,
            logo: logo?.url,
            valueScore: calculateValueScore(provider, propertyConfig),
          },
        ],
      };
    } else if (showableNotAvailableProviders.includes(displayName)) {
      return {
        ...info,
        notAvailableProviders: [
          ...(info.notAvailableProviders || []),
          {
            id,
            name: displayName || name,
            logo: logo?.url,
            valueScore: calculateValueScore(provider, propertyConfig),
          },
        ],
      };
    } else {
      return info;
    }
  }, {});

  const scrapingVariation = await getVariation(
    'tvi_svbt_spectrum_strategy_test'
  );

  const { availableProviders } = providerInfo;

  const offerlessAvailalbleProviders = availableProviders.reduce(
    (accu, provider) => {
      const hasNoOffers = !offers.find(
        ({ providerCode }) => providerCode === provider.id
      );
      return hasNoOffers ? accu.concat(provider) : accu;
    },
    []
  );

  const dummyOffers = offerlessAvailalbleProviders.map((provider) => ({
    id: `${Math.floor(Math.random() * 1000) * -1}`,
    code: `${provider.id}-offer`,
    name: `${provider.name} Offer`,
    providerCode: provider.id,
    pricing: {
      term: {
        unit: 'Month',
      },
    },
    products: {
      internet: {},
    },
  }));

  const transformedOffers = [...offers, ...dummyOffers].map((offer = {}) => {
    let introductoryPrice = offer.pricing?.charges?.find(
      ({ type }) => type === 'short_term'
    );

    let longTermPrice = offer.pricing?.charges?.find(
      ({ type }) => type === 'long_term'
    );

    if (introductoryPrice && !longTermPrice) {
      longTermPrice = introductoryPrice;
      introductoryPrice = undefined;
    }

    const { provider } = providerSummaries.find(
      ({ provider }) => provider.code === offer.providerCode
    );

    const isScrapedSpectrumOffer =
      provider.code === 'spectrum' && scrapingVariation === 'spectrum_svbt';

    return {
      id: offer.code,
      code: offer.code,
      name: offer.name,
      price: longTermPrice?.amount,
      ...(isScrapedSpectrumOffer ? { source: 'scraped' } : {}),
      valueScore: calculateValueScore(provider, propertyConfig),
      highlights: offer.highlights,
      extendedHighlights: offer.extendedHighlights,
      promotions: offer.promotions?.map(({ description, disclaimer }) => {
        return {
          promotion: description,
          disclaimer: disclaimer?.disclaimer,
        };
      }),
      term: offer.pricing?.term?.unit,
      ...(introductoryPrice
        ? {
            introductoryPrice: {
              price: introductoryPrice.amount,
              term: introductoryPrice.term.unit, // Capitalize
              duration: introductoryPrice.term.length,
              savingsPercent: longTermPrice
                ? Math.floor(
                    ((longTermPrice.amount - introductoryPrice.amount) /
                      longTermPrice.amount) *
                      100
                  )
                : undefined,
            },
          }
        : {}),
      services: {
        internet: offer.products.internet
          ? {
              kind: offer.products.internet.type?.toUpperCase?.(),
              ...(offer.products.internet.downloadSpeed?.value
                ? {
                    speed: {
                      mbps: offer.products.internet.downloadSpeed?.value,
                      tier: 'MAXIMUM',
                    },
                  }
                : {}),
            }
          : null,
        tv: offer.products.tv
          ? {
              additionalLanguages: offer.products.tv.additionalLanguages,
              kind: offer.products.tv.type?.toUpperCase?.(),
              ...(offer.products.tv.channels?.count
                ? {
                    channels: {
                      count: offer.products.tv.channels?.count,
                    },
                  }
                : {}),
            }
          : null,
        phone: offer.products.phone
          ? {
              kind: offer.products.phone.type?.toUpperCase?.(),
            }
          : null,
      },
      provider: {
        id: provider.code,
        logo: provider.logo?.url,
        name: provider.displayName || provider.name,
        featured: Boolean(provider.featured),
        exclusive: Boolean(provider.exclusive),
        featuredKind: provider.featured
          ? 'property_featured'
          : provider.exclusive
          ? 'property_exclusive'
          : null,
      },
    };
  });

  return {
    offers: transformedOffers,
    ...providerInfo,
  };
};

export const trimOfferForReservation = (offer) => ({
  ...offer,
  source: undefined,
});
